import React from 'react';
import { makeStyles } from 'react-scale/components/design-system/styles';
import {
  getPreorderWorkflowTraversal,
  TraversedTaskerWorkflowStep,
  filterCheckEveryAnnotationSteps,
  getWorkflowAncestors,
} from '@scale/shared/taskerWorkflow/TaskerWorkflow';
import WorkflowStepNode from './WorkflowStepNode';
import { WorkflowHook } from '../Sidebar/GuidedWorkflowPanel/useWorkflow';

const useStyles = makeStyles(theme => ({
  workflowProgressBarContainer: {
    display: 'flex',
    flexDirection: 'row',
    borderTop: `1px solid ${theme.colors.gray10}`,
  },
}));

interface WorkflowProgressBarProps {
  workflowHook: WorkflowHook;
  workflowStepContainerRef: React.RefObject<HTMLDivElement>;
}
const WorkflowProgressBar: React.FC<WorkflowProgressBarProps> = props => {
  const classes = useStyles();

  const { workflowState, workflow } = props.workflowHook;

  // Only include subtrees related to the ancestor of the active step
  const subtreesToInclude = getWorkflowAncestors(workflow!, workflowState.activeStepId).map(
    node => node.id,
  );
  // TODO: remove this once workflow bar is gated on workflow existence
  const uncollapsedNodes = getPreorderWorkflowTraversal(
    workflow ?? { id: 'root', name: 'root step', description: '', steps: [] },
    {
      subtreesToInclude,
    },
  );

  const nodesToDisplay = filterCheckEveryAnnotationSteps(
    uncollapsedNodes,
    workflowState.currentStep,
  );

  const getNodeState = (node: TraversedTaskerWorkflowStep) => {
    if (node.id === workflowState.activeStepId) {
      return 'active';
    }
    if (workflowState.completedStepIds.includes(node.id)) {
      return 'completed';
    }
    const parentNodeId = node.id.split('-').slice(0, -1).join('-');
    const activeStepParentNodeId = workflowState.activeStepId.split('-').slice(0, -1).join('-');
    if (
      (node.depth > 1 && parentNodeId === activeStepParentNodeId) ||
      activeStepParentNodeId.includes(node.id)
    ) {
      return 'sibling';
    }

    return 'unstarted';
  };

  return (
    <div className={classes.workflowProgressBarContainer}>
      {nodesToDisplay.map(node => {
        const state = getNodeState(node);
        return (
          <WorkflowStepNode
            key={node.id}
            state={state}
            node={node}
            workflowHook={props.workflowHook}
            workflowStepContainerRef={state === 'active' ? props.workflowStepContainerRef : null}
          />
        );
      })}
    </div>
  );
};

export default React.memo(WorkflowProgressBar);
